import { useEffect } from 'react';
import { useOnlineStatus } from './useOnlineStatus';
import { isDesktopRuntime } from './db';
import { drainQueue } from './syncEngine';

const SAFETY_NET_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Mounted once at the app root (App.tsx). Drains the outbox whenever
 * connectivity is restored, and on a periodic safety net while online --
 * cheap since drainQueue() returns immediately when there's nothing due
 * for replay (pending items backing off via next_retry_at are skipped,
 * see queue.ts/syncEngine.ts). Under normal operation nothing new gets
 * enqueued while already online (every offline call site checks isOffline
 * before choosing enqueue vs. the live API call), so the safety net
 * mostly guards against a drain attempt itself failing partway through
 * (e.g. a network blip mid-sync leaving some rows still 'pending') and
 * against a backed-off auto-retry's window elapsing between renders.
 *
 * Both effects gate on the same heartbeat-backed `isOnline` (from
 * useOnlineStatus, not raw navigator.onLine) -- navigator.onLine only
 * reflects the network interface, not real backend reachability (e.g. a
 * captive portal or VPN-without-route still reports "online"), so using
 * it here would let the interval kick off doomed drain attempts.
 */
export function useSyncEngine(): void {
  const isOnline = useOnlineStatus();

  useEffect(() => {
    if (!isDesktopRuntime() || !isOnline) return;
    drainQueue();
  }, [isOnline]);

  useEffect(() => {
    if (!isDesktopRuntime() || !isOnline) return;
    const interval = setInterval(drainQueue, SAFETY_NET_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isOnline]);
}
